/**
 * ariaDomainTokens — single source of truth mapping an ARIA *domain* to its
 * visual identity (color used by the ambient field, constellation nodes,
 * artifact borders and data-viz series).
 *
 * The color IS information: every surface of the Mission Control canvas tints
 * itself to the domain of whatever ARIA is currently reasoning about, so the
 * user reads "this is about treasury / payroll / tax" pre-attentively.
 *
 * Two consumption shapes are exposed because Tailwind can't generate classes
 * dynamically:
 *   - `hex` / `soft` / `glow` — raw values for inline styles, SVG and Recharts.
 *   - `text` / `border` / `badge` / `dot` / `ring` — full static class strings
 *     (every literal appears verbatim so the JIT compiler keeps them).
 *
 * Resolution order (see `resolveDomain`): explicit tool name → UI module →
 * default ('general', the supervisor's own color).
 */

export const DEFAULT_DOMAIN = 'general';

export const ARIA_DOMAINS = {
  treasury: {
    key: 'treasury',
    label: 'Tesorería',
    hex: '#3b82f6',
    soft: '#93c5fd',
    glow: 'rgba(59,130,246,0.45)',
    text: 'text-blue-600 dark:text-blue-300',
    border: 'border-blue-500/30',
    badge: 'bg-blue-500/10 text-blue-600 dark:text-blue-300 ring-1 ring-blue-500/20',
    dot: 'bg-blue-500',
    ring: 'ring-blue-500/40',
  },
  invoicing: {
    key: 'invoicing',
    label: 'Facturación',
    hex: '#10b981',
    soft: '#6ee7b7',
    glow: 'rgba(16,185,129,0.45)',
    text: 'text-emerald-600 dark:text-emerald-300',
    border: 'border-emerald-500/30',
    badge: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-300 ring-1 ring-emerald-500/20',
    dot: 'bg-emerald-500',
    ring: 'ring-emerald-500/40',
  },
  payroll: {
    key: 'payroll',
    label: 'Nóminas / RRHH',
    hex: '#6366f1',
    soft: '#a5b4fc',
    glow: 'rgba(99,102,241,0.45)',
    text: 'text-indigo-600 dark:text-indigo-300',
    border: 'border-indigo-500/30',
    badge: 'bg-indigo-500/10 text-indigo-600 dark:text-indigo-300 ring-1 ring-indigo-500/20',
    dot: 'bg-indigo-500',
    ring: 'ring-indigo-500/40',
  },
  fiscal: {
    key: 'fiscal',
    label: 'Fiscal / AEAT',
    hex: '#f59e0b',
    soft: '#fcd34d',
    glow: 'rgba(245,158,11,0.45)',
    text: 'text-amber-600 dark:text-amber-300',
    border: 'border-amber-500/30',
    badge: 'bg-amber-500/10 text-amber-600 dark:text-amber-300 ring-1 ring-amber-500/20',
    dot: 'bg-amber-500',
    ring: 'ring-amber-500/40',
  },
  crm: {
    key: 'crm',
    label: 'CRM',
    hex: '#ec4899',
    soft: '#f9a8d4',
    glow: 'rgba(236,72,153,0.45)',
    text: 'text-pink-600 dark:text-pink-300',
    border: 'border-pink-500/30',
    badge: 'bg-pink-500/10 text-pink-600 dark:text-pink-300 ring-1 ring-pink-500/20',
    dot: 'bg-pink-500',
    ring: 'ring-pink-500/40',
  },
  accounting: {
    key: 'accounting',
    label: 'Contabilidad',
    hex: '#06b6d4',
    soft: '#67e8f9',
    glow: 'rgba(6,182,212,0.45)',
    text: 'text-cyan-600 dark:text-cyan-300',
    border: 'border-cyan-500/30',
    badge: 'bg-cyan-500/10 text-cyan-600 dark:text-cyan-300 ring-1 ring-cyan-500/20',
    dot: 'bg-cyan-500',
    ring: 'ring-cyan-500/40',
  },
  projects: {
    key: 'projects',
    label: 'Proyectos',
    hex: '#14b8a6',
    soft: '#5eead4',
    glow: 'rgba(20,184,166,0.45)',
    text: 'text-teal-600 dark:text-teal-300',
    border: 'border-teal-500/30',
    badge: 'bg-teal-500/10 text-teal-600 dark:text-teal-300 ring-1 ring-teal-500/20',
    dot: 'bg-teal-500',
    ring: 'ring-teal-500/40',
  },
  inventory: {
    key: 'inventory',
    label: 'Inventario',
    hex: '#f97316',
    soft: '#fdba74',
    glow: 'rgba(249,115,22,0.45)',
    text: 'text-orange-600 dark:text-orange-300',
    border: 'border-orange-500/30',
    badge: 'bg-orange-500/10 text-orange-600 dark:text-orange-300 ring-1 ring-orange-500/20',
    dot: 'bg-orange-500',
    ring: 'ring-orange-500/40',
  },
  expenses: {
    key: 'expenses',
    label: 'Gastos',
    hex: '#f43f5e',
    soft: '#fda4af',
    glow: 'rgba(244,63,94,0.45)',
    text: 'text-rose-600 dark:text-rose-300',
    border: 'border-rose-500/30',
    badge: 'bg-rose-500/10 text-rose-600 dark:text-rose-300 ring-1 ring-rose-500/20',
    dot: 'bg-rose-500',
    ring: 'ring-rose-500/40',
  },
  analytics: {
    key: 'analytics',
    label: 'Analítica',
    hex: '#0ea5e9',
    soft: '#7dd3fc',
    glow: 'rgba(14,165,233,0.45)',
    text: 'text-sky-600 dark:text-sky-300',
    border: 'border-sky-500/30',
    badge: 'bg-sky-500/10 text-sky-600 dark:text-sky-300 ring-1 ring-sky-500/20',
    dot: 'bg-sky-500',
    ring: 'ring-sky-500/40',
  },
  performance: {
    key: 'performance',
    label: 'Rendimiento',
    hex: '#84cc16',
    soft: '#bef264',
    glow: 'rgba(132,204,22,0.45)',
    text: 'text-lime-600 dark:text-lime-300',
    border: 'border-lime-500/30',
    badge: 'bg-lime-500/10 text-lime-600 dark:text-lime-300 ring-1 ring-lime-500/20',
    dot: 'bg-lime-500',
    ring: 'ring-lime-500/40',
  },
  automation: {
    key: 'automation',
    label: 'Automatización',
    hex: '#64748b',
    soft: '#cbd5e1',
    glow: 'rgba(100,116,139,0.45)',
    text: 'text-slate-600 dark:text-slate-300',
    border: 'border-slate-500/30',
    badge: 'bg-slate-500/10 text-slate-600 dark:text-slate-300 ring-1 ring-slate-500/20',
    dot: 'bg-slate-500',
    ring: 'ring-slate-500/40',
  },
  grants: {
    key: 'grants',
    label: 'Subvenciones',
    hex: '#d946ef',
    soft: '#f0abfc',
    glow: 'rgba(217,70,239,0.45)',
    text: 'text-fuchsia-600 dark:text-fuchsia-300',
    border: 'border-fuchsia-500/30',
    badge: 'bg-fuchsia-500/10 text-fuchsia-600 dark:text-fuchsia-300 ring-1 ring-fuchsia-500/20',
    dot: 'bg-fuchsia-500',
    ring: 'ring-fuchsia-500/40',
  },
  general: {
    key: 'general',
    label: 'ARIA',
    hex: '#8b5cf6',
    soft: '#c4b5fd',
    glow: 'rgba(139,92,246,0.45)',
    text: 'text-violet-600 dark:text-violet-300',
    border: 'border-violet-500/30',
    badge: 'bg-violet-500/10 text-violet-600 dark:text-violet-300 ring-1 ring-violet-500/20',
    dot: 'bg-violet-500',
    ring: 'ring-violet-500/40',
  },
};

// The 14 backend specialist ids (specialistRegistry.ts) → visual domain key.
// `hr` and `banking` reuse the payroll/treasury palette (same business color);
// the rest are 1:1. This is how an agent card / trace node picks its color.
export const AGENT_VISUAL_DOMAIN = {
  fiscal: 'fiscal',
  hr: 'payroll',
  accounting: 'accounting',
  banking: 'treasury',
  invoicing: 'invoicing',
  expenses: 'expenses',
  crm: 'crm',
  analytics: 'analytics',
  projects: 'projects',
  inventory: 'inventory',
  performance: 'performance',
  automation: 'automation',
  grants: 'grants',
  general: 'general',
};

/** Visual tokens for a backend agent/specialist id (falls back to the default). */
export function agentTokens(agentId) {
  return domainTokens(AGENT_VISUAL_DOMAIN[agentId] ?? agentId);
}

// Ordered keyword rules — first match wins. Tool names are snake_case Spanish
// (`recordatorio_facturas_vencidas`) or English (`forecast_cash_flow`); both
// vocabularies are covered.
const TOOL_DOMAIN_RULES = [
  [/factur|invoice|cobr|remind|draft|verifactu|ticketbai/i, 'invoicing'],
  [/cash|tesorer|banc|bank|forecast|concilia|liquidez|runway|psd2|sepa/i, 'treasury'],
  [/nomin|payroll|emplead|employee|salar|rrhh|\bhr\b|retenc|seguridad_social|ausenci|absence|contrat/i, 'payroll'],
  [/fiscal|aeat|\bsii\b|\biva\b|impuesto|modelo_?\d|\btax\b|\b303\b|\b347\b|withhold/i, 'fiscal'],
  [/crm|cliente|customer|deal|pipeline|oportun|lead|contacto|quote|presupuesto/i, 'crm'],
  [/asiento|conta|account|journal|mayor|diario|balance|\bpgc\b|cierre|amortiz/i, 'accounting'],
  [/proyecto|project|tarea|\btask\b|kanban|rentabilidad/i, 'projects'],
  [/inventar|stock|producto|product|almacen/i, 'inventory'],
];

const MODULE_DOMAIN_RULES = [
  [/factur|invoice/i, 'invoicing'],
  [/banc|tesorer|cash/i, 'treasury'],
  [/nomin|rrhh|\bhr\b|payroll|emplead/i, 'payroll'],
  [/fiscal|aeat|verifactu/i, 'fiscal'],
  [/crm|cliente/i, 'crm'],
  [/contab|account/i, 'accounting'],
  [/proyecto|project/i, 'projects'],
  [/inventar|producto/i, 'inventory'],
];

function matchRules(value, rules) {
  if (!value) return null;
  // Strip diacritics so accented module labels ('Nóminas') match ASCII rules.
  const norm = value.normalize('NFD').replace(/[̀-ͯ]/g, '');
  for (const [re, domain] of rules) {
    if (re.test(norm)) return domain;
  }
  return null;
}

/** Domain for a given tool name (e.g. `forecast_cash_flow` → 'treasury'). */
export function domainForTool(toolName) {
  return matchRules(toolName, TOOL_DOMAIN_RULES) ?? DEFAULT_DOMAIN;
}

/** Domain for a UI module label (e.g. `Nóminas` → 'payroll'). */
export function domainForModule(moduleName) {
  return matchRules(moduleName, MODULE_DOMAIN_RULES) ?? DEFAULT_DOMAIN;
}

/**
 * Resolve the active domain. Tool wins (most specific signal of what ARIA is
 * doing right now), then the UI module, then the supervisor default.
 */
export function resolveDomain({ tool, module } = {}) {
  return (
    matchRules(tool, TOOL_DOMAIN_RULES) ??
    matchRules(module, MODULE_DOMAIN_RULES) ??
    DEFAULT_DOMAIN
  );
}

/** Token object for a domain key, falling back to the default. */
export function domainTokens(domainKey) {
  return ARIA_DOMAINS[domainKey] ?? ARIA_DOMAINS[DEFAULT_DOMAIN];
}

// Backend `toolDomain()` buckets (inverse of DOMAIN_TOOLS in toolSelector.ts)
// → our visual domains. The backend is the source of truth for what a tool
// belongs to, so when the SSE event carries a domain we trust it; only the
// coarse 'general' / unknown falls through to the regex heuristic.
const BACKEND_DOMAIN_TO_VISUAL = {
  invoicing: 'invoicing',
  invoice_email: 'invoicing',
  rectification: 'invoicing',
  revenue: 'invoicing',
  expenses: 'expenses',
  hr: 'payroll',
  banking: 'treasury',
  finance: 'accounting', // supervisor calls the accounting domain "finance"
  analytics: 'analytics',
  scenarios: 'analytics',
  currency: 'analytics',
  accounting: 'accounting',
  accounting_provisions: 'accounting',
  accounting_adjustments: 'accounting',
  accounting_advances: 'accounting',
  accounting_leasing: 'accounting',
  accounting_impairment: 'accounting',
  crm: 'crm',
  inventory: 'inventory',
  products: 'inventory',
  purchasing: 'inventory',
  fiscal: 'fiscal',
  fiscal_modelos: 'fiscal',
  tax: 'fiscal',
  projects: 'projects',
  performance: 'performance',
  automation: 'automation',
  grants: 'grants',
};

/**
 * Map a backend tool-domain bucket to a visual domain, or null if unknown /
 * 'general' (so the caller can fall back to `domainForTool`).
 */
export function visualDomainFromBackend(backendDomain) {
  if (!backendDomain || backendDomain === 'general') return null;
  return BACKEND_DOMAIN_TO_VISUAL[backendDomain] ?? null;
}
